'use client';

import { useEffect, useState } from 'react';
import { publicPriceExtrasLines, type PublicPriceLocationExtras } from '@/utils/billing/publicPriceExtras';

/** Shown when the settings cannot be read: true whatever they are. */
export const PRICE_EXTRAS_UNKNOWN_TEXT = 'Sales tax and fees may be added at checkout.';

/**
 * #5237: "+ tax and Admin Fee" under a public price — what checkout adds at this class's or
 * lesson's locations. Nothing while it loads, and nothing when checkout adds nothing.
 */
export default function PublicPriceExtrasNote({ programId }: { programId: string }) {
  const [state, setState] = useState<{ programId: string; lines: string[] | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/public/programs/price-extras?programId=${encodeURIComponent(programId)}`);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data?.success || !Array.isArray(data.locations)) {
          setState({ programId, lines: null });
          return;
        }
        setState({ programId, lines: publicPriceExtrasLines(data.locations as PublicPriceLocationExtras[]) });
      } catch {
        if (!cancelled) setState({ programId, lines: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [programId]);

  if (!state || state.programId !== programId) return null;
  const lines = state.lines ?? [PRICE_EXTRAS_UNKNOWN_TEXT];
  if (lines.length === 0) return null;
  return (
    <div className="-mt-3 mb-4 space-y-0.5" data-testid="public-price-extras">
      {lines.map((line) => (
        <p key={line} className="text-xs font-medium text-gray-600 text-right">
          {line}
        </p>
      ))}
    </div>
  );
}
